import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, SimpleGrid, Spinner,
  Badge, Icon, Divider, Select, Alert, AlertIcon, Flex, Tooltip,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
} from '@chakra-ui/react';
import {
  FiRefreshCw, FiCheckCircle, FiXCircle, FiLink, FiUsers,
  FiClock, FiActivity, FiShield,
} from 'react-icons/fi';

const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  };
  const instanceId = localStorage.getItem('instance_id');
  if (instanceId) headers['X-Tenant-ID'] = instanceId;
  return headers;
};

const apiFetch = (url) =>
  fetch(url, { headers: getAuthHeaders() }).then(r => r.json());

const formatTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const BLOCK_COLORS = {
  registration: 'blue', introduction: 'teal', panel: 'purple',
  talk: 'orange', workshop: 'yellow', networking: 'green',
  break: 'gray', qa: 'cyan', closing: 'red',
  setup: 'gray', teardown: 'gray', custom: 'pink',
};

function StatCard({ icon, label, value, color = 'orange' }) {
  return (
    <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
      <HStack spacing={3} mb={2}>
        <Icon as={icon} boxSize={5} color={`${color}.500`} />
        <Text fontSize="sm" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.05em">
          {label}
        </Text>
      </HStack>
      <Text fontSize="3xl" fontWeight="bold" color={`${color}.600`}>{value}</Text>
    </Box>
  );
}

export default function RundownStatus() {
  const [instances, setInstances] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [webhooks, setWebhooks] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingInstances, setLoadingInstances] = useState(true);
  const [instanceError, setInstanceError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // Load event instances for the picker
  useEffect(() => {
    apiFetch('/api/event_instances/?page_size=200')
      .then(d => {
        if (d.detail) throw new Error(d.detail);
        const items = Array.isArray(d) ? d : (d.results || []);
        setInstances(items);
        if (items.length > 0 && !selectedId) setSelectedId(String(items[0].id));
      })
      .catch(err => setInstanceError(err.message || 'Failed to load event instances'))
      .finally(() => setLoadingInstances(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(() => {
    if (!selectedId) return;
    setLoading(true);
    Promise.all([
      apiFetch(`/api/rundown_webhooks/?event_instance=${selectedId}`),
      apiFetch(`/api/webhook_deliveries/?event_instance=${selectedId}`),
      apiFetch(`/api/v1/rundown-viewers/?event_instance=${selectedId}`),
    ])
      .then(([wh, dl, vc]) => {
        setWebhooks(wh.results || []);
        setDeliveries(dl.results || []);
        setViewerCount(vc.count || 0);
        setLastRefreshed(new Date());
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll viewer count every 10 s
  useEffect(() => {
    if (!selectedId) return;
    const interval = setInterval(() => {
      apiFetch(`/api/v1/rundown-viewers/?event_instance=${selectedId}`)
        .then(d => setViewerCount(d.count || 0))
        .catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedId]);

  // Derived stats
  const totalDeliveries = deliveries.length;
  const successCount = deliveries.filter(d => d.success).length;
  const successRate = totalDeliveries > 0 ? Math.round((successCount / totalDeliveries) * 100) : null;
  const avgMs = totalDeliveries > 0
    ? Math.round(deliveries.filter(d => d.response_ms != null).reduce((a, d) => a + d.response_ms, 0) /
        Math.max(1, deliveries.filter(d => d.response_ms != null).length))
    : null;

  // Per-webhook delivery stats
  const webhookStats = webhooks.map(w => {
    const wdl = deliveries.filter(d => d.webhook === w.id);
    const ok = wdl.filter(d => d.success).length;
    const err = wdl.filter(d => !d.success).length;
    const lastDl = wdl[0]; // already sorted newest first
    return { ...w, ok, err, total: wdl.length, lastDl };
  });

  const selectedInstance = instances.find(i => String(i.id) === selectedId);

  return (
    <Box>
      {/* Header */}
      <HStack justify="space-between" mb={6} flexWrap="wrap" gap={4}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Rundown Status</Heading>
          <Text color="gray.500">
            Live viewer connections, webhook delivery history
            {lastRefreshed && ` — refreshed ${lastRefreshed.toLocaleTimeString()}`}
          </Text>
        </VStack>
        <HStack spacing={3}>
          {loadingInstances ? (
            <Spinner size="sm" color="orange.500" />
          ) : instanceError ? (
            <Text fontSize="sm" color="red.500">{instanceError}</Text>
          ) : instances.length === 0 ? (
            <Text fontSize="sm" color="gray.400">No event instances found</Text>
          ) : (
            <Select
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              size="sm"
              w="320px"
              bg="white"
              borderRadius="md"
            >
              {instances.map(i => {
                const dateLabel = i.start_date
                  ? new Date(i.start_date).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
                  : null;
                const label = i.name
                  ? `${i.name}${dateLabel ? ` — ${dateLabel}` : ''}`
                  : dateLabel || `Instance #${i.id}`;
                return (
                  <option key={i.id} value={i.id}>{label}</option>
                );
              })}
            </Select>
          )}
          <Button leftIcon={<FiRefreshCw />} colorScheme="orange" size="sm"
            onClick={fetchData} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </HStack>

      {instanceError ? (
        <Alert status="error" borderRadius="lg"><AlertIcon />{instanceError}</Alert>
      ) : !selectedId ? (
        <Alert status="info" borderRadius="lg"><AlertIcon />
          {instances.length === 0 && !loadingInstances
            ? 'No event instances found for this account.'
            : 'Select an event instance to view rundown status.'}
        </Alert>
      ) : loading && deliveries.length === 0 ? (
        <Box textAlign="center" py={20}><Spinner size="xl" color="orange.500" /></Box>
      ) : (
        <>
          {/* Stat cards */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={8}>
            <StatCard
              icon={FiUsers}
              label="Live Viewers"
              value={viewerCount}
              color={viewerCount > 0 ? 'teal' : 'gray'}
            />
            <StatCard
              icon={FiLink}
              label="Webhooks"
              value={webhooks.filter(w => w.is_active).length}
              color="purple"
            />
            <StatCard
              icon={FiActivity}
              label="Success Rate"
              value={successRate != null ? `${successRate}%` : '—'}
              color={successRate == null ? 'gray' : successRate >= 90 ? 'green' : successRate >= 70 ? 'orange' : 'red'}
            />
            <StatCard
              icon={FiClock}
              label="Avg Response"
              value={avgMs != null ? `${avgMs}ms` : '—'}
              color={avgMs == null ? 'gray' : avgMs < 500 ? 'green' : avgMs < 2000 ? 'orange' : 'red'}
            />
          </SimpleGrid>

          {/* Webhook list */}
          <Box bg="white" borderRadius="lg" boxShadow="md" p={6} mb={6}>
            <HStack mb={4} spacing={2}>
              <Icon as={FiLink} color="purple.500" />
              <Heading size="md">Webhooks</Heading>
              {webhooks.length === 0 && (
                <Badge colorScheme="gray">none configured</Badge>
              )}
            </HStack>
            {webhooks.length === 0 ? (
              <Text color="gray.400" fontSize="sm">No webhooks configured for this event instance.</Text>
            ) : (
              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>URL</Th>
                      <Th>Status</Th>
                      <Th>Signing</Th>
                      <Th isNumeric>Deliveries</Th>
                      <Th isNumeric>✓</Th>
                      <Th isNumeric>✗</Th>
                      <Th>Last delivery</Th>
                      <Th>Last result</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {webhookStats.map(w => (
                      <Tr key={w.id}>
                        <Td maxW="260px">
                          <Text fontFamily="mono" fontSize="xs" isTruncated title={w.url}>{w.url}</Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={w.is_active ? 'green' : 'gray'}>
                            {w.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </Td>
                        <Td>
                          <Tooltip label={w.has_secret ? 'HMAC-SHA256 signing enabled (X-Webhook-Signature)' : 'No signing secret configured'}>
                            <HStack spacing={1}>
                              <Icon as={FiShield} color={w.has_secret ? 'green.400' : 'gray.300'} boxSize={3} />
                              <Text fontSize="xs" color={w.has_secret ? 'green.600' : 'gray.400'}>
                                {w.has_secret ? 'Signed' : 'None'}
                              </Text>
                            </HStack>
                          </Tooltip>
                        </Td>
                        <Td isNumeric>{w.total}</Td>
                        <Td isNumeric>
                          <HStack justify="flex-end" spacing={1}>
                            {w.ok > 0 && <Icon as={FiCheckCircle} color="green.400" boxSize={3} />}
                            <Text color="green.600" fontWeight={w.ok > 0 ? 'semibold' : 'normal'}>
                              {w.ok || '—'}
                            </Text>
                          </HStack>
                        </Td>
                        <Td isNumeric>
                          <HStack justify="flex-end" spacing={1}>
                            {w.err > 0 && <Icon as={FiXCircle} color="red.400" boxSize={3} />}
                            <Text color={w.err > 0 ? 'red.600' : 'gray.400'} fontWeight={w.err > 0 ? 'semibold' : 'normal'}>
                              {w.err || '—'}
                            </Text>
                          </HStack>
                        </Td>
                        <Td color="gray.500" fontSize="xs">
                          {w.lastDl ? formatDateTime(w.lastDl.created_at) : '—'}
                        </Td>
                        <Td>
                          {w.lastDl ? (
                            <HStack spacing={1}>
                              <Icon as={w.lastDl.success ? FiCheckCircle : FiXCircle}
                                color={w.lastDl.success ? 'green.400' : 'red.400'} boxSize={3} />
                              <Text fontSize="xs" color={w.lastDl.success ? 'green.600' : 'red.600'} fontWeight="semibold">
                                {w.lastDl.status_code ?? 'err'}
                              </Text>
                              {w.lastDl.response_ms != null && (
                                <Text fontSize="xs" color="gray.400">{w.lastDl.response_ms}ms</Text>
                              )}
                            </HStack>
                          ) : '—'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* Delivery log */}
          <Box bg="white" borderRadius="lg" boxShadow="md" p={6}>
            <HStack mb={4} justify="space-between">
              <HStack spacing={2}>
                <Icon as={FiActivity} color="orange.500" />
                <Heading size="md">Recent Deliveries</Heading>
                {deliveries.length > 0 && (
                  <Badge colorScheme="gray">{deliveries.length}</Badge>
                )}
              </HStack>
              {deliveries.length > 0 && (
                <HStack spacing={4} fontSize="xs" color="gray.500">
                  <HStack spacing={1}>
                    <Icon as={FiCheckCircle} color="green.400" boxSize={3} />
                    <Text>{successCount} succeeded</Text>
                  </HStack>
                  <HStack spacing={1}>
                    <Icon as={FiXCircle} color="red.400" boxSize={3} />
                    <Text>{totalDeliveries - successCount} failed</Text>
                  </HStack>
                </HStack>
              )}
            </HStack>

            {deliveries.length === 0 ? (
              <Box textAlign="center" py={8} color="gray.400">
                <Icon as={FiActivity} boxSize={8} mb={3} opacity={0.4} />
                <Text>No deliveries yet. They appear here once the live view advances to a new block.</Text>
              </Box>
            ) : (
              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Time</Th>
                      <Th>Block</Th>
                      <Th>Type</Th>
                      <Th>#</Th>
                      <Th>Webhook URL</Th>
                      <Th>Status</Th>
                      <Th isNumeric>ms</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {deliveries.map(d => (
                      <Tr key={d.id} _hover={{ bg: 'gray.50' }}>
                        <Td whiteSpace="nowrap" color="gray.500" fontSize="xs">
                          {formatDateTime(d.created_at)}
                        </Td>
                        <Td maxW="160px">
                          <Tooltip label={d.block_title}>
                            <Text isTruncated fontSize="sm">{d.block_title || '—'}</Text>
                          </Tooltip>
                        </Td>
                        <Td>
                          {d.block_type ? (
                            <Badge colorScheme={BLOCK_COLORS[d.block_type] || 'gray'} fontSize="2xs">
                              {d.block_type}
                            </Badge>
                          ) : '—'}
                        </Td>
                        <Td color="gray.500" fontSize="xs">{d.block_index ?? '—'}</Td>
                        <Td maxW="220px">
                          <Tooltip label={d.webhook_url}>
                            <Text fontFamily="mono" fontSize="xs" isTruncated color="gray.600">
                              {d.webhook_url}
                            </Text>
                          </Tooltip>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Icon as={d.success ? FiCheckCircle : FiXCircle}
                              color={d.success ? 'green.400' : 'red.400'} boxSize={3} />
                            <Text fontSize="sm" fontWeight="semibold"
                              color={d.success ? 'green.600' : 'red.600'}>
                              {d.status_code ?? (d.error_message ? 'err' : '—')}
                            </Text>
                          </HStack>
                          {d.error_message && (
                            <Tooltip label={d.error_message}>
                              <Text fontSize="xs" color="red.400" isTruncated maxW="140px">
                                {d.error_message}
                              </Text>
                            </Tooltip>
                          )}
                        </Td>
                        <Td isNumeric color="gray.500" fontSize="xs">
                          {d.response_ms != null ? `${d.response_ms}ms` : '—'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
